/**
 * Select-all state for a grid's selection bar.
 *
 * "Select all" means every row matching the grid's live filters, not the
 * loaded page — so the flag is scoped to the filter set it was made under
 * and clears whenever that set changes. This hook owns the flag, the
 * request/clear callbacks, and that reset, and hands back the exact
 * `selectAll` bag EntityGrid's selection option takes.
 *
 * Contains:
 * - useGridSelectAll — (resetKey) → { active, onRequest, onClear, selectAll }
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

/**
 * @param {*} resetKey - The grid's filter identity (typically the memoized
 *   extraParams object); any referential change clears an active select-all.
 * @returns {{ active: boolean, onRequest: Function, onClear: Function,
 *   selectAll: { active: boolean, onRequest: Function, onClear: Function } }}
 */
export default function useGridSelectAll(resetKey) {
  const [active, setActive] = useState(false);
  const onRequest = useCallback(() => setActive(true), []);
  const onClear = useCallback(() => setActive(false), []);

  useEffect(() => {
    setActive(false);
  }, [resetKey]);

  const selectAll = useMemo(() => ({ active, onRequest, onClear }), [active, onRequest, onClear]);
  return { active, onRequest, onClear, selectAll };
}
